import React from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconsSend from '@mui/icons-material/Send';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import DatePicker from '@mui/lab/DatePicker';
import ButtonSubmit from '../../../../../components/_button/ButtonSubmit';
import Select, {
  IValue,
} from '../../../../../components/_select/_module/Select';
import { IProductVariationsSubTypeInput } from '../../../../../../graphql/type/_inputTypes/productVariations';

interface IProps {
  onCancel: () => void;
  item: IProductVariationsSubTypeInput;
  onChange: (item: IProductVariationsSubTypeInput) => void;
}

export default function FormUpdateProductInventory(props: IProps) {
  const { t } = useTranslation();
  const { item, onCancel, onChange: propOnChange } = props;
  const attributes = item.attributes ? item.attributes : {};

  function onChange(
    field:
      | 'inventoryLowStock'
      | 'inventoryNoStock'
      | 'inventoryInStock'
      | 'inventoryReserved'
      | 'inventoryLeadTime'
      | 'inventoryAbilityNumber'
      | 'inventoryAbilityTime'
      | 'inventoryAvailability'
      | 'inventoryDateFrom'
      | 'inventoryDateTo',
    newValue: any
  ) {
    const tempItem = { ...item };

    tempItem[field] = newValue;
    propOnChange(tempItem);
  }

  return (
    <>
      <DialogTitle>
        {t('update')} {attributes.sku} {t('inventory')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid container item xs={12} spacing={1}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">{t('inventory')}</Typography>
            </Grid>
            <Grid item xs={4} sm={4} md={4} lg={4}>
              <FormControl fullWidth>
                <TextField
                  label={t('in_stock')}
                  type="number"
                  value={item.inventoryInStock}
                  onChange={(e) => {
                    onChange('inventoryInStock', parseInt(e.target.value, 10));
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4} sm={4} md={4} lg={4}>
              <FormControl fullWidth>
                <TextField
                  label={t('reserved')}
                  type="number"
                  inputProps={{ style: { textAlign: 'right' } }}
                  value={item.inventoryReserved}
                  onChange={(e) => {
                    onChange('inventoryReserved', parseInt(e.target.value, 10));
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4} sm={4} md={4} lg={4}>
              <FormControl fullWidth>
                <TextField
                  label={t('lead_time (day)')}
                  type="number"
                  inputProps={{ style: { textAlign: 'right' } }}
                  value={item.inventoryLeadTime}
                  onChange={(e) => {
                    onChange('inventoryLeadTime', parseInt(e.target.value, 10));
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">{t('availability')}</Typography>
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={6}>
              <FormControl fullWidth>
                <TextField
                  placeholder={t('supply_ability')}
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{t('per')}</InputAdornment>
                    ),
                  }}
                  value={item.inventoryAbilityNumber}
                  onChange={(e) => {
                    onChange(
                      'inventoryAbilityNumber',
                      parseInt(e.target.value, 10)
                    );
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={6}>
              <FormControl fullWidth>
                <Select<IValue>
                  isClearable
                  options={[
                    {
                      label: t('millisecond'),
                      value: '0',
                    },
                    {
                      label: t('second'),
                      value: '1',
                    },
                    {
                      label: t('minute'),
                      value: '2',
                    },
                    {
                      label: t('hour'),
                      value: '3',
                    },
                    {
                      label: t('day'),
                      value: '4',
                    },
                    {
                      label: t('week'),
                      value: '5',
                    },
                    {
                      label: t('month'),
                      value: '6',
                    },
                    {
                      label: t('year'),
                      value: '7',
                    },
                  ]}
                  fullWidth
                  menuPosition="fixed"
                  value={item.inventoryAbilityTime}
                  onChange={(option) => {
                    onChange(
                      'inventoryAbilityTime',
                      option ? option.value : ''
                    );
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Select<IValue>
                  isClearable
                  options={[
                    { value: '1', label: t('in_stock') },
                    { value: '2', label: t('limited_stock') },
                    { value: '3', label: t('24_hours') },
                    { value: '4', label: t('48_hours') },
                    { value: '5', label: t('2_3_days') },
                    { value: '6', label: t('3_5_days') },
                    { value: '7', label: t('7_days') },
                    { value: '8', label: t('14_days') },
                    { value: '9', label: t('1_4_weeks') },
                    { value: '10', label: t('1_2_months') },
                    { value: '11', label: t('more_then_3_months') },
                    { value: '12', label: t('sold_out') },
                    { value: '13', label: t('pre_order') },
                    { value: '14', label: t('in_store_only') },
                    { value: '15', label: t('online_only') },
                    { value: '16', label: t('out_of_stock') },
                    { value: '17', label: t('discontinued') },
                  ]}
                  label={t('current_availability')}
                  fullWidth
                  menuPosition="fixed"
                  value={item.inventoryAvailability}
                  onChange={(option) => {
                    onChange(
                      'inventoryAvailability',
                      option ? option.value : ''
                    );
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={1}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">{t('date_available')}</Typography>
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={6}>
              <FormControl fullWidth>
                <DatePicker
                  inputFormat="dd/MMM/yyyy"
                  renderInput={(tempProps) => <TextField {...tempProps} />}
                  label={t('from')}
                  value={item.inventoryDateFrom || null}
                  onChange={(date) => {
                    onChange('inventoryDateFrom', date || null);
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={6}>
              <FormControl fullWidth>
                <DatePicker
                  inputFormat="dd/MMM/yyyy"
                  renderInput={(tempProps) => <TextField {...tempProps} />}
                  label={t('to')}
                  value={item.inventoryDateTo || null}
                  onChange={(date) => {
                    onChange('inventoryDateTo', date || null);
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={1}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">{t('notification')}</Typography>
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={6}>
              <FormControl fullWidth>
                <TextField
                  label={t('low_stock')}
                  type="number"
                  inputProps={{ style: { textAlign: 'right' } }}
                  value={item.inventoryLowStock}
                  onChange={(e) => {
                    onChange('inventoryLowStock', parseInt(e.target.value, 10));
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={6}>
              <FormControl fullWidth>
                <TextField
                  label={t('no_stock')}
                  type="number"
                  inputProps={{ style: { textAlign: 'right' } }}
                  value={item.inventoryNoStock}
                  onChange={(e) => {
                    onChange('inventoryNoStock', parseInt(e.target.value, 10));
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <ButtonSubmit
              fullWidth
              variant="contained"
              color="primary"
              onClick={onCancel}
              label={t('close')}
              icon={<IconsSend />}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
}
